import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Pull the top of a scroller down and it refetches (docs/1 § Surfaces).
 *
 * A HOOK OVER THE SCROLLER RATHER THAN A COMPONENT PER SCREEN, because there is
 * exactly one scroller in this app — the `<main>` in `AppShell` — and every tab is
 * rendered into it. Written per screen this would be five copies of a touch handler,
 * five thresholds, and four screens that quietly never got one.
 *
 * WHAT IT DOES NOT DO is decide what "refresh" means. It reports a gesture and awaits
 * a promise; `AppShell` refetches the queries that are currently mounted. That keeps
 * the pixels, the gesture and the data in three places that can each be read alone.
 *
 * THE LISTENERS ARE NATIVE AND `touchmove` IS NOT PASSIVE, which is the only
 * non-obvious line here. React registers `touchmove` passively at the root, so
 * `preventDefault` from an `onTouchMove` prop is ignored and the browser scrolls the
 * page out from under the gesture. Attaching to the element is what makes the pull
 * a pull.
 */

/** How far the indicator travels before the gesture arms, in CSS pixels. */
const THRESHOLD = 56;

/** Where the indicator waits while the refresh runs. Under `THRESHOLD`: the pull
 *  settles back a little when the finger lifts, so releasing is visible. */
const REST = 44;

/** The finger moves further than the indicator does, so the pull has weight. */
const RESISTANCE = 0.55;

/** Below this a touch is a tap or a scroll, never a pull. */
const SLOP = 8;

/**
 * How far the finger is followed before the indicator stops moving, so a long drag
 * cannot throw the disc down the page.
 *
 * IT MUST CLEAR `THRESHOLD / RESISTANCE` WITH ROOM TO SPARE, which is the arithmetic
 * that has to be checked when any of these three is touched: at 96 the travel capped
 * at 52.8px against a threshold of 56, so the gesture followed the finger beautifully
 * and could not arm at all — a pull that never refreshes, which is precisely the bug
 * this whole feature is about.
 */
const MAX = 160;

/**
 * The shortest time the refreshing state is shown.
 *
 * A REFETCH OFF A WARM CACHE COMES BACK IN 30ms, and the indicator appearing and
 * vanishing inside two frames reads as a glitch rather than as an answer — so the
 * gesture looks broken precisely when the network is fast.
 */
const MIN_VISIBLE_MS = 450;

export type PullToRefresh = {
  /**
   * Put this on the scroll container.
   *
   * A CALLBACK REF RATHER THAN A `useRef`, AND THAT IS NOT A STYLE CHOICE. The
   * scroller mounts inside the login gate, which renders a loading state first — so
   * an effect keyed on a `RefObject` ran once while `ref.current` was still null,
   * returned early, and never ran again, because nothing in its dependencies changed
   * when the element finally appeared. The gesture was silently dead on every screen.
   * A callback ref makes the element itself the dependency, which is the only version
   * of this that cannot be wrong.
   */
  ref: (node: HTMLElement | null) => void;
  /** How far the indicator has been pulled, in CSS pixels. */
  travel: number;
  /**
   * The height of the band the content makes room for, in CSS pixels — `REST` while
   * the refresh runs and 0 the rest of the time, so the page moves once per gesture
   * rather than once per frame. See `PullIndicator`.
   */
  band: number;
  /** Release now and it refreshes. The arrow turns over on it. */
  armed: boolean;
  /** The refresh is running. */
  refreshing: boolean;
  /** A finger is down: the indicator tracks it and must not be animated. */
  dragging: boolean;
};

/**
 * Whether a touch that started here belongs to the container's own scroll.
 *
 * A NESTED SCROLLER OWNS ITS OWN OVERSCROLL. The dove tab's panel is a scrolling
 * sheet inside a screen that does not scroll, so without this a downward drag inside
 * an open dialog refreshed the app behind it.
 */
function ownsPull(target: EventTarget | null, container: HTMLElement): boolean {
  let node = target instanceof Element ? target : null;
  while (node && node !== container) {
    if (
      node.scrollHeight > node.clientHeight &&
      /auto|scroll/.test(getComputedStyle(node).overflowY)
    ) {
      return false;
    }
    node = node.parentElement;
  }
  return container.scrollTop === 0;
}

export function usePullToRefresh({
  onRefresh,
  enabled = true,
}: {
  onRefresh: () => Promise<unknown>;
  /** Off where there is nothing to refetch, and off over a half-written letter. */
  enabled?: boolean;
}): PullToRefresh {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [travel, setTravel] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [dragging, setDragging] = useState(false);

  /* The handlers are attached once and read the latest values from refs. Mirrored
     rather than in the dependency array, because re-attaching a `touchmove`
     listener mid-gesture drops the gesture. */
  const travelRef = useRef(0);
  const refreshingRef = useRef(false);
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;

  const pull = useCallback((to: number) => {
    travelRef.current = to;
    setTravel(to);
  }, []);

  useEffect(() => {
    if (!el || !enabled) return;

    /** The y the pull is measured from, or null when this touch is not a pull. */
    let origin: number | null = null;
    let pulling = false;

    const start = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (refreshingRef.current || !touch || e.touches.length !== 1) return;
      origin = ownsPull(e.target, el) ? touch.clientY : null;
      pulling = false;
    };

    const move = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (origin === null || !touch) return;
      const dy = touch.clientY - origin;

      if (!pulling) {
        /* Upward first means the finger is scrolling, and a touch only gets one
           chance to be a pull. */
        if (dy < 0) origin = null;
        if (dy < SLOP) return;
        pulling = true;
        setDragging(true);
      }

      /* The scroll is cancelled for as long as the pull lasts, so the page cannot
         rubber-band behind the indicator. */
      e.preventDefault();
      pull(Math.min(dy - SLOP, MAX) * RESISTANCE);
    };

    const end = () => {
      const armed = pulling && travelRef.current >= THRESHOLD;
      origin = null;
      pulling = false;
      setDragging(false);

      if (!armed) {
        pull(0);
        return;
      }

      refreshingRef.current = true;
      setRefreshing(true);
      pull(REST);

      void Promise.all([
        refreshRef.current(),
        new Promise((done) => setTimeout(done, MIN_VISIBLE_MS)),
      ]).finally(() => {
        refreshingRef.current = false;
        setRefreshing(false);
        pull(0);
      });
    };

    el.addEventListener('touchstart', start, { passive: true });
    el.addEventListener('touchmove', move, { passive: false });
    el.addEventListener('touchend', end);
    el.addEventListener('touchcancel', end);
    return () => {
      el.removeEventListener('touchstart', start);
      el.removeEventListener('touchmove', move);
      el.removeEventListener('touchend', end);
      el.removeEventListener('touchcancel', end);
    };
  }, [el, enabled, pull]);

  return {
    ref: setEl,
    travel,
    band: refreshing ? REST : 0,
    armed: travel >= THRESHOLD,
    refreshing,
    dragging,
  };
}
